import { inject, Injectable } from '@angular/core';
import { ChainCampaigns, ChainPrizes, ChainRules, Items } from 'tilby-models';
import { PrizesFields } from '../models/fidelity-prize.model';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { TranslateService } from '@ngx-translate/core';
import { MathUtils } from 'src/app/shared/utils';

export interface FidelityDataMap {
  'campaigns': ChainCampaigns[];
  'rules': ChainRules[];
  'rulesMap': Record<string | number, ChainRules>;
  'prizes': PrizesFields[];
  'prizesMap': Record<string | number, PrizesFields>;
  'prizesByCampaign': Record<string | number, PrizesFields[]>;
  'items': Items[];
  'itemsMap': Record<string, Items>;
}

export type FidelityDataSetKey = keyof FidelityDataMap;
export type FidelityDataSetValue = FidelityDataMap[FidelityDataSetKey];

@Injectable({
  providedIn: 'root'
})
export class FidelityDataCatalogService {

  private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
  private readonly translate = inject(TranslateService);

  private _fidelityDataCatalog = new Map<FidelityDataSetKey, FidelityDataSetValue>();

  get fidelityDataCatalog(): Map<FidelityDataSetKey, FidelityDataSetValue> {
    return this._fidelityDataCatalog;
  }


  addDataToCatalog(key: FidelityDataSetKey, value: FidelityDataSetValue): void {
    this._fidelityDataCatalog.set(key, value);
  }

  /** The stored data itself, not a copy: only for lookups that do not modify it (copying the whole item list is expensive) */
  peekDataByKey<K extends FidelityDataSetKey>(key: K): Readonly<FidelityDataMap[K]> | undefined {
    return this._fidelityDataCatalog.get(key) as Readonly<FidelityDataMap[K]> | undefined;
  }

  getDataByKey<K extends FidelityDataSetKey>(key: K): FidelityDataMap[K] | undefined {
    const originalArray =  this._fidelityDataCatalog.get(key);
    if (originalArray) {
      return structuredClone(originalArray) as FidelityDataMap[K];
    }
    return undefined;
  }

  private getPrizeType(prize: ChainPrizes): string {
    const itemsMap = (this._fidelityDataCatalog.get('itemsMap') || {}) as Record<string, Items>;

    switch (prize.type) {
      case 'discount_fix':
        return this.translate.instant('FIDELITY.PRIZES.PRIZE_DIALOG.DISCOUNT', { 
          value: this.tilbyCurrencyPipe.transform(prize.discount_amount, 'EUR') 
        });
        
      case 'discount_perc':
        const value = MathUtils.round(prize.discount_amount || 0) + '%';
        return this.translate.instant('FIDELITY.PRIZES.PRIZE_DIALOG.DISCOUNT', { value });
        
      case 'gift':
        const itemName = itemsMap[prize.item_sku!]?.name || '';
        return this.translate.instant('FIDELITY.PRIZES.PRIZE_DIALOG.GIFT', { value: itemName });
        
      default:
        return '';
    }
  }

  setProcessedPrizes(prizes: ChainPrizes[]): void {
    const extendedPrizes = (prizes as ChainPrizes[]).map(p => ({
      ...p,
      _typeString: this.getPrizeType(p)
    })) as PrizesFields[];

    this.addDataToCatalog('prizes', extendedPrizes);

    const map = Object.fromEntries(extendedPrizes.map(p => [p.id, p]));
    this.addDataToCatalog('prizesMap', map);

    const grouped = extendedPrizes.reduce((acc, p) => {
      const cid = p.campaign_id;
      if (!acc[cid]) acc[cid] = [];
      acc[cid].push(p);
      return acc;
    }, {} as Record<string | number, PrizesFields[]>);
    
    this.addDataToCatalog('prizesByCampaign', grouped);
  }

  setProcessedRules(rules: ChainRules[]): void {
    this.addDataToCatalog('rules', rules);

    const map = Object.fromEntries(rules.map(r => [r.id, r]));
    this.addDataToCatalog('rulesMap', map);
  }

  setProcessedItems(rawItems: Items[]): void {
    const processedItems = rawItems
      .filter(item => !!item.sku)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const itemsMap = Object.fromEntries(
      processedItems.map(item => [item.sku, item])
    );

    this.addDataToCatalog('items', processedItems);
    this.addDataToCatalog('itemsMap', itemsMap);
  }
}
